import React from 'react';
import { Gift, CheckCircle2, Download, Smartphone, Target, Printer, Search, Phone, ClipboardCheck, Table2, CalendarDays, FileCheck, Building2, FolderOpen, Clock, RotateCcw, Wifi, Tablet, ListChecks, Tag, MessageSquare, ArrowRight } from 'lucide-react';
type BonusSectionProps = {
  totalPrice: number;
  totalValue: number;
  bump1Checked: boolean;
  bump2Checked: boolean;
  checkoutUrl: string;
};
export function BonusSection({
  totalPrice,
  totalValue,
  bump1Checked,
  bump2Checked,
  checkoutUrl
}: BonusSectionProps) {
  return <section className="bg-white py-12 md:py-28 px-6" data-id="element-254">
      <div className="max-w-5xl mx-auto" data-id="element-255">
        {/* 1. INTRO HEADER */}
        <div className="text-center mb-16" data-id="element-256">
          <h2 className="font-serif text-3xl md:text-5xl font-bold text-brand-dark mb-4 uppercase tracking-wide" data-id="element-257">
            You're Getting 3 Free Bonuses Today
          </h2>
          <p className="text-xl md:text-2xl text-brand-gold font-bold mb-6" data-id="element-258">
            Included With Your Purchase — Total Bonus Value: $66.74
          </p>
          <div className="inline-block bg-red-100 text-red-800 px-6 py-2 rounded-full font-bold text-lg mb-6" data-id="element-259">
            WAIT — Before You Check Out, Read This.
          </div>
          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed mb-4" data-id="element-260">
            When you grab The Senior Benefit Unlocker Guide today at the limited
            price of $14.00, you are not just getting a guide.
          </p>
          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed mb-4" data-id="element-261">
            You are getting a complete{' '}
            <strong className="text-brand-dark" data-id="element-262">3-bonus action system</strong> —
            every tool, script, checklist, and tracker you need to go from "I
            don't know where to start" to "my application is submitted and I
            know exactly what to do next."
          </p>
          <p className="text-lg md:text-xl text-brand-dark font-bold max-w-3xl mx-auto leading-relaxed mb-4" data-id="element-263">
            Every single bonus listed below is included at no extra charge.
          </p>
          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed" data-id="element-264">
            Here is everything that is waiting for you on the other side of this
            page.
          </p>
        </div>

        {/* 2. BONUS #1 */}
        <div className="mb-20" data-id="element-265">
          <div className="inline-flex items-center gap-2 bg-brand-gold/10 border border-brand-gold/30 text-brand-gold font-bold text-sm uppercase tracking-wider px-5 py-2 rounded-full mb-8" data-id="element-266">
            <Gift className="w-4 h-4" data-id="element-267" />
            FREE BONUS #1 (Value: $47.00) — Included Today Only
          </div>
          <div className="flex flex-col lg:flex-row gap-10" data-id="element-268">
            <div className="flex-1 lg:max-w-md" data-id="element-269">
              <img src="https://cdn.magicpatterns.com/uploads/nCmH9cbXUUHEmC4u2b2WEd/Bonus_1_The_Senior_Benefits_Bonus_Image.png" alt="The Missing Notice Fast-Track Kit" className="w-full h-auto rounded-xl shadow-2xl" data-id="element-270" />
            </div>
            <div className="flex-1" data-id="element-271">
              <h3 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-2" data-id="element-272">
                The Missing Notice Fast-Track Kit
              </h3>
              <p className="text-gray-500 italic mb-4" data-id="element-273">(60+ Edition)</p>
              <p className="font-serif text-xl font-bold italic text-brand-dark mb-4" data-id="element-274">
                The letter the system never sent you — finally in your hands.
              </p>
              <div className="text-lg text-gray-700 leading-relaxed space-y-4 mb-6" data-id="element-275">
                <p data-id="element-276">
                  Somewhere in a government database, your name is attached to
                  programs you never applied for. Credits you qualify for.
                  Refunds that require nothing more than a form and a phone
                  call.
                </p>
                <p data-id="element-277">
                  Nobody sent you the letter. Nobody called. The system does not
                  alert you when you qualify — it waits for you to find it.
                </p>
                <p className="font-medium text-brand-dark p-5 bg-brand-cream border-l-4 border-brand-gold rounded-r-lg" data-id="element-278">
                  This kit is the map.
                </p>
                <p data-id="element-279">
                  In 15 minutes, you will know exactly which programs to check,
                  exactly what to say when you call, and exactly how to track
                  every case number so nothing falls through the cracks.
                </p>
              </div>

              <p className="text-sm font-bold uppercase tracking-wider text-brand-gold mb-4" data-id="element-280">
                What's Inside Bonus #1:
              </p>
              <div className="space-y-4" data-id="element-281">
                <div className="flex items-start gap-4 p-4 bg-brand-cream rounded-xl" data-id="element-282">
                  <Search className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" data-id="element-283" />
                  <div data-id="element-284">
                    <h4 className="font-bold text-brand-dark" data-id="element-285">
                      The 15-Minute "Missing Notice" Scan
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-286">
                      A simple 3-phase process that identifies your Top 2
                      benefit areas before you make a single call. No guessing.
                      No rabbit holes. Just a clear starting point.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-brand-cream rounded-xl" data-id="element-287">
                  <Phone className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" data-id="element-288" />
                  <div data-id="element-289">
                    <h4 className="font-bold text-brand-dark" data-id="element-290">
                      Word-For-Word Phone Scripts (3 Complete Scripts)
                    </h4>
                    <p className="text-gray-600 text-sm mb-2" data-id="element-291">
                      Read them exactly as written. You don't need to figure out
                      what to say. We already did that for you.
                    </p>
                    <ul className="text-sm text-gray-500 space-y-1 ml-1" data-id="element-292">
                      <li className="flex items-center gap-2" data-id="element-293">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-gold flex-shrink-0" data-id="element-294"></span>
                        Script A: Utility & Energy Relief
                      </li>
                      <li className="flex items-center gap-2" data-id="element-295">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-gold flex-shrink-0" data-id="element-296"></span>
                        Script B: Medicare-Related Savings & Prescription Help
                      </li>
                      <li className="flex items-center gap-2" data-id="element-297">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-gold flex-shrink-0" data-id="element-298"></span>
                        Script C: Food Support for Seniors
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-brand-cream rounded-xl" data-id="element-299">
                  <ClipboardCheck className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" data-id="element-300" />
                  <div data-id="element-301">
                    <h4 className="font-bold text-brand-dark" data-id="element-302">
                      The "Don't Get Bounced" Mini-Checklist
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-303">
                      6 questions to ask on every call so you always leave with
                      a program name, a next step, and a case number — not a
                      dead end.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-brand-cream rounded-xl" data-id="element-304">
                  <Table2 className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" data-id="element-305" />
                  <div data-id="element-306">
                    <h4 className="font-bold text-brand-dark" data-id="element-307">
                      The Calls + Applications Tracker (Printable)
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-308">
                      A clean, large-font table to record every call, every
                      agent name, every reference number, and every follow-up
                      date. Works on paper or on screen.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-brand-cream rounded-xl" data-id="element-309">
                  <CalendarDays className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" data-id="element-310" />
                  <div data-id="element-311">
                    <h4 className="font-bold text-brand-dark" data-id="element-312">
                      The Simple 7-Day Action Plan
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-313">
                      Day-by-day steps from your first call to your first
                      application submitted. No overwhelm. No confusion. Just
                      one clear thing to do each day.
                    </p>
                  </div>
                </div>
              </div>
              <p className="mt-6 text-brand-dark font-bold italic text-lg" data-id="element-314">
                The Senior Benefit Unlocker Guide shows you what is available.
                The Missing Notice Fast-Track Kit shows you exactly how to start
                today.
              </p>
            </div>
          </div>
        </div>

        {/* 3. BONUS #2 */}
        <div className="mb-20" data-id="element-315">
          <div className="inline-flex items-center gap-2 bg-brand-gold/10 border border-brand-gold/30 text-brand-gold font-bold text-sm uppercase tracking-wider px-5 py-2 rounded-full mb-8" data-id="element-316">
            <Gift className="w-4 h-4" data-id="element-317" />
            FREE BONUS #2 (Value: $9.87) — Included Today Only
          </div>
          <div className="flex flex-col lg:flex-row-reverse gap-10" data-id="element-318">
            <div className="flex-1 lg:max-w-md" data-id="element-319">
              <img src="https://cdn.magicpatterns.com/uploads/81kPB1y4UmWRXL498Edovr/Bonus_2_The_Zero_Mistake_Benefits_Application_Checklist.png" alt="The Zero Mistake Benefits Application Checklist" className="w-full h-auto rounded-xl shadow-2xl" data-id="element-320" />
            </div>
            <div className="flex-1" data-id="element-321">
              <h3 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-2" data-id="element-322">
                The Zero Mistake Benefits Application Checklist
              </h3>
              <p className="text-gray-500 italic mb-4" data-id="element-323">(60+ Edition)</p>
              <p className="font-serif text-xl font-bold italic text-brand-dark mb-4" data-id="element-324">
                The single most common reason seniors get denied is not
                disqualification. It is paperwork errors.
              </p>
              <div className="text-lg text-gray-700 leading-relaxed space-y-4 mb-6" data-id="element-325">
                <p data-id="element-326">
                  A wrong date. A missing signature. An income figure written in
                  the wrong box. An application submitted to the wrong office.
                  These are not your fault — the forms are designed for people
                  who fill them out every day. You are not one of those people.
                </p>
                <p className="font-medium text-brand-dark p-5 bg-brand-cream border-l-4 border-brand-gold rounded-r-lg" data-id="element-327">
                  This checklist is.
                </p>
                <p data-id="element-328">
                  Before you submit a single application, run it through this
                  checklist. In 10 minutes, you will know your application is
                  clean, complete, and going to the right place — giving you the
                  highest possible chance of approval on the first attempt.
                </p>
              </div>

              <p className="text-sm font-bold uppercase tracking-wider text-brand-gold mb-4" data-id="element-329">
                What's Inside Bonus #2:
              </p>
              <div className="space-y-4" data-id="element-330">
                <div className="flex items-start gap-4 p-4 bg-brand-cream rounded-xl" data-id="element-331">
                  <FileCheck className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" data-id="element-332" />
                  <div data-id="element-333">
                    <h4 className="font-bold text-brand-dark" data-id="element-334">
                      The Pre-Submission Application Review (10-Minute
                      Checklist)
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-335">
                      Goes field by field before you submit anything. Catches
                      the most common errors that trigger automatic denials —
                      missing dates, incorrect income figures, unsigned
                      sections, and wrong program selection.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-brand-cream rounded-xl" data-id="element-336">
                  <Building2 className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" data-id="element-337" />
                  <div data-id="element-338">
                    <h4 className="font-bold text-brand-dark" data-id="element-339">
                      The "Right Office, Right Form" Verification Guide
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-340">
                      Confirms you are submitting to the correct agency, the
                      correct program, and using the correct form version — not
                      an outdated one from two years ago.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-brand-cream rounded-xl" data-id="element-341">
                  <FolderOpen className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" data-id="element-342" />
                  <div data-id="element-343">
                    <h4 className="font-bold text-brand-dark" data-id="element-344">
                      The Document Readiness Checklist
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-345">
                      Every document you may need — proof of income, ID, utility
                      bills, Medicare card, lease or deed — organized by program
                      type so you are never caught unprepared.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-brand-cream rounded-xl" data-id="element-346">
                  <Clock className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" data-id="element-347" />
                  <div data-id="element-348">
                    <h4 className="font-bold text-brand-dark" data-id="element-349">
                      The Follow-Up Timeline Guide
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-350">
                      What to do if you don't hear back in 7 days. In 14 days.
                      In 30 days. Exactly what to say and who to call so your
                      application doesn't disappear into a system and stay
                      there.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-brand-cream rounded-xl" data-id="element-351">
                  <RotateCcw className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" data-id="element-352" />
                  <div data-id="element-353">
                    <h4 className="font-bold text-brand-dark" data-id="element-354">
                      The "Second Attempt" Protocol
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-355">
                      If you were denied — or told you don't qualify — this
                      section walks you through how to ask the right questions,
                      identify whether it was a program error, and reapply
                      correctly.
                    </p>
                  </div>
                </div>
              </div>
              <p className="mt-6 text-brand-dark font-bold italic text-lg" data-id="element-356">
                The Senior Benefit Unlocker Guide finds the programs. The Zero
                Mistake Benefits Application Checklist makes sure your
                application is bulletproof before it leaves your hands.
              </p>
            </div>
          </div>
        </div>

        {/* 4. BONUS #3 */}
        <div className="mb-20" data-id="element-357">
          <div className="inline-flex items-center gap-2 bg-brand-gold/10 border border-brand-gold/30 text-brand-gold font-bold text-sm uppercase tracking-wider px-5 py-2 rounded-full mb-8" data-id="element-358">
            <Gift className="w-4 h-4" data-id="element-359" />
            FREE BONUS #3 (Value: $9.87) — Included Today Only
          </div>
          <div className="flex flex-col lg:flex-row gap-10" data-id="element-360">
            <div className="flex-1 lg:max-w-md" data-id="element-361">
              <img src="https://cdn.magicpatterns.com/uploads/4YCtKkWhWqdpuZdLQj55wW/Bonus_3_The_Senior_Tech_Saver_Guide.png" alt="The Senior Tech Saver Guide" className="w-full h-auto rounded-xl shadow-2xl" data-id="element-362" />
            </div>
            <div className="flex-1" data-id="element-363">
              <h3 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-2" data-id="element-364">
                The Senior Tech Saver Guide
              </h3>
              <p className="text-gray-500 italic mb-4" data-id="element-365">(60+ Edition)</p>
              <p className="font-serif text-xl font-bold italic text-brand-dark mb-4" data-id="element-366">
                Your phone, your tablet, and your computer are worth hundreds of
                dollars a year in savings you are not using.
              </p>
              <div className="text-lg text-gray-700 leading-relaxed space-y-4 mb-6" data-id="element-367">
                <p data-id="element-368">
                  Most seniors are overpaying for their phone plan by $30 to $60
                  every month. They are paying full price for apps,
                  subscriptions, and services that offer senior discounts — but
                  never advertise them. They are missing out on free tablets,
                  discounted internet, and government-subsidized connectivity
                  programs that exist specifically for people 60+.
                </p>
                <p className="font-medium text-brand-dark p-5 bg-brand-cream border-l-4 border-brand-gold rounded-r-lg" data-id="element-369">
                  Nobody told you. The companies certainly didn't. This guide
                  does.
                </p>
              </div>

              <p className="text-sm font-bold uppercase tracking-wider text-brand-gold mb-4" data-id="element-370">
                What's Inside Bonus #3:
              </p>
              <div className="space-y-4" data-id="element-371">
                <div className="flex items-start gap-4 p-4 bg-brand-cream rounded-xl" data-id="element-372">
                  <Wifi className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" data-id="element-373" />
                  <div data-id="element-374">
                    <h4 className="font-bold text-brand-dark" data-id="element-375">
                      The Senior Phone Plan Savings Audit
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-376">
                      A simple comparison of the top low-cost and
                      government-subsidized phone plans available to seniors.
                      Includes what to say when you call your current carrier to
                      reduce your bill immediately.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-brand-cream rounded-xl" data-id="element-377">
                  <Tablet className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" data-id="element-378" />
                  <div data-id="element-379">
                    <h4 className="font-bold text-brand-dark" data-id="element-380">
                      The Free & Discounted Tech Programs List
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-381">
                      Government and nonprofit programs that provide free or
                      heavily discounted tablets, computers, and internet access
                      to seniors 60+ — including eligibility requirements and
                      how to apply.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-brand-cream rounded-xl" data-id="element-382">
                  <ListChecks className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" data-id="element-383" />
                  <div data-id="element-384">
                    <h4 className="font-bold text-brand-dark" data-id="element-385">
                      The Subscription Audit Checklist
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-386">
                      A one-page checklist to identify every active subscription
                      you are paying for — and which ones offer senior
                      discounts, free tiers, or cancellation options that put
                      money back in your pocket immediately.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-brand-cream rounded-xl" data-id="element-387">
                  <Tag className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" data-id="element-388" />
                  <div data-id="element-389">
                    <h4 className="font-bold text-brand-dark" data-id="element-390">
                      The "Senior Discount You Never Knew Existed" List
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-391">
                      A curated list of software, streaming, and service
                      providers that offer unpublished senior discounts —
                      available by simply asking or using the correct discount
                      code.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-brand-cream rounded-xl" data-id="element-392">
                  <MessageSquare className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" data-id="element-393" />
                  <div data-id="element-394">
                    <h4 className="font-bold text-brand-dark" data-id="element-395">
                      The Internet Bill Reduction Script
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-396">
                      Word-for-word what to say to your internet provider to
                      reduce your monthly bill — including the specific program
                      names to ask about and the exact phrases that trigger
                      retention offers.
                    </p>
                  </div>
                </div>
              </div>
              <p className="mt-6 text-brand-dark font-bold italic text-lg" data-id="element-397">
                The Senior Benefit Unlocker Guide finds the big credits. The
                Senior Tech Saver Guide finds the smaller ones hiding in your
                monthly bills — adding up to $300 to $700 per year.
              </p>
            </div>
          </div>
        </div>

        {/* 5. VALUE TABLE */}
        <div className="max-w-3xl mx-auto mb-16" data-id="element-398">
          <h3 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-8 text-center" data-id="element-399">
            Here's Everything You're Getting Today
          </h3>
          <div className="bg-white rounded-2xl border-2 border-brand-gold overflow-hidden shadow-xl" data-id="element-400">
            <table className="w-full text-left border-collapse" data-id="element-401">
              <thead data-id="element-402">
                <tr className="bg-brand-cream border-b-2 border-brand-gold" data-id="element-403">
                  <th className="py-4 px-6 text-lg font-bold text-brand-dark" data-id="element-404">
                    What You Get
                  </th>
                  <th className="py-4 px-6 text-lg font-bold text-brand-dark text-right" data-id="element-405">
                    Retail Value
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700" data-id="element-406">
                <tr className="border-b border-gray-200" data-id="element-407">
                  <td className="py-4 px-6 font-medium" data-id="element-408">
                    The Senior Benefit Unlocker Guide (35+ Page PDF)
                  </td>
                  <td className="py-4 px-6 text-right font-medium" data-id="element-409">$19.00</td>
                </tr>
                <tr className="border-b border-gray-200" data-id="element-410">
                  <td className="py-4 px-6" data-id="element-411">
                    <span className="font-bold text-brand-gold" data-id="element-412">BONUS #1:</span>{' '}
                    The Missing Notice Fast-Track Kit
                  </td>
                  <td className="py-4 px-6 text-right font-medium" data-id="element-413">$47.00</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50" data-id="element-414">
                  <td className="py-3 px-6 text-sm text-gray-500 pl-10" data-id="element-415">
                    15-Min Scan + 3 Scripts + Tracker + 7-Day Plan
                  </td>
                  <td className="py-3 px-6 text-right text-sm text-gray-500" data-id="element-416">
                    Included
                  </td>
                </tr>
                <tr className="border-b border-gray-200" data-id="element-417">
                  <td className="py-4 px-6" data-id="element-418">
                    <span className="font-bold text-brand-gold" data-id="element-419">BONUS #2:</span>{' '}
                    The Zero Mistake Benefits Application Checklist
                  </td>
                  <td className="py-4 px-6 text-right font-medium" data-id="element-420">$9.87</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50" data-id="element-421">
                  <td className="py-3 px-6 text-sm text-gray-500 pl-10" data-id="element-422">
                    Pre-Submit Review + Document Checklist + Follow-Up Timeline
                  </td>
                  <td className="py-3 px-6 text-right text-sm text-gray-500" data-id="element-423">
                    Included
                  </td>
                </tr>
                <tr className="border-b border-gray-200" data-id="element-424">
                  <td className="py-4 px-6" data-id="element-425">
                    <span className="font-bold text-brand-gold" data-id="element-426">BONUS #3:</span>{' '}
                    The Senior Tech Saver Guide
                  </td>
                  <td className="py-4 px-6 text-right font-medium" data-id="element-427">$9.87</td>
                </tr>
                <tr className="border-b-2 border-brand-gold bg-gray-50" data-id="element-428">
                  <td className="py-3 px-6 text-sm text-gray-500 pl-10" data-id="element-429">
                    Phone Plan Audit + Free Tech Programs + Internet Bill Script
                  </td>
                  <td className="py-3 px-6 text-right text-sm text-gray-500" data-id="element-430">
                    Included
                  </td>
                </tr>
                {bump1Checked && <tr className="border-b border-gray-200 bg-brand-gold/5" data-id="element-431">
                    <td className="py-4 px-6" data-id="element-432">
                      <span className="font-bold text-brand-dark" data-id="element-433">
                        UPGRADE #1:
                      </span>{' '}
                      The Hidden Wealth Retirement Audit
                      <div className="text-sm text-gray-500 mt-1" data-id="element-434">
                        7-Source Checklist + Pension Trace + Tax Deduction Audit
                      </div>
                    </td>
                    <td className="py-4 px-6 text-right font-medium" data-id="element-435">
                      <span className="line-through text-gray-400 mr-2" data-id="element-436">
                        $47.00
                      </span>
                      <span className="text-brand-gold font-bold" data-id="element-437">$9.00</span>
                    </td>
                  </tr>}
                {bump2Checked && <tr className="border-b border-gray-200 bg-brand-gold/5" data-id="element-438">
                    <td className="py-4 px-6" data-id="element-439">
                      <span className="font-bold text-brand-dark" data-id="element-440">
                        UPGRADE #2:
                      </span>{' '}
                      The Priority Advocacy Master System
                      <div className="text-sm text-gray-500 mt-1" data-id="element-441">
                        5-Level Escalation + Appeal Scripts + Advocacy Directory
                      </div>
                    </td>
                    <td className="py-4 px-6 text-right font-medium" data-id="element-442">
                      <span className="line-through text-gray-400 mr-2" data-id="element-443">
                        $47.00
                      </span>
                      <span className="text-brand-gold font-bold" data-id="element-444">$9.00</span>
                    </td>
                  </tr>}
                <tr className="bg-brand-dark text-white" data-id="element-445">
                  <td className="py-5 px-6 font-bold text-xl" data-id="element-446">TOTAL VALUE</td>
                  <td className="py-5 px-6 text-right font-bold text-xl text-brand-gold" data-id="element-447">
                    <span className="line-through text-gray-400 mr-2 text-lg" data-id="element-448">
                      ${totalValue.toFixed(2)}
                    </span>
                  </td>
                </tr>
                <tr className="bg-brand-gold text-brand-dark" data-id="element-449">
                  <td className="py-6 px-6 font-black text-2xl uppercase tracking-wide" data-id="element-450">
                    YOUR PRICE TODAY
                  </td>
                  <td className="py-6 px-6 text-right font-black text-3xl" data-id="element-451">
                    ${totalPrice}.00
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-center text-gray-500 text-sm mt-4" data-id="element-452">
            Limited-time offer — while available.
          </p>
        </div>

        {/* 6. BADGES */}
        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mb-12" data-id="element-453">
          <div className="flex items-center gap-2 bg-brand-cream px-5 py-3 rounded-full border border-gray-200 shadow-sm" data-id="element-454">
            <Download className="w-5 h-5 text-brand-gold" data-id="element-455" />
            <span className="font-bold text-sm uppercase tracking-wider text-brand-dark" data-id="element-456">
              Instant Download
            </span>
          </div>
          <div className="flex items-center gap-2 bg-brand-cream px-5 py-3 rounded-full border border-gray-200 shadow-sm" data-id="element-457">
            <Smartphone className="w-5 h-5 text-brand-gold" data-id="element-458" />
            <span className="font-bold text-sm uppercase tracking-wider text-brand-dark" data-id="element-459">
              Phone / Tablet / Computer
            </span>
          </div>
          <div className="flex items-center gap-2 bg-brand-cream px-5 py-3 rounded-full border border-gray-200 shadow-sm" data-id="element-460">
            <Target className="w-5 h-5 text-brand-gold" data-id="element-461" />
            <span className="font-bold text-sm uppercase tracking-wider text-brand-dark" data-id="element-462">
              Beginner-Friendly
            </span>
          </div>
          <div className="flex items-center gap-2 bg-brand-cream px-5 py-3 rounded-full border border-gray-200 shadow-sm" data-id="element-463">
            <Printer className="w-5 h-5 text-brand-gold" data-id="element-464" />
            <span className="font-bold text-sm uppercase tracking-wider text-brand-dark" data-id="element-465">
              All Bonuses Printable
            </span>
          </div>
        </div>

        {/* 7. CTA BUTTON */}
        <div className="text-center" data-id="element-466">
          <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-full sm:w-auto px-10 py-5 text-xl font-bold text-brand-dark bg-brand-gold hover:bg-brand-gold-hover transition-colors rounded-lg shadow-xl hover:shadow-2xl transform hover:-translate-y-1" data-id="element-467">
            Yes — Get Everything for ${totalPrice}.00 Right Now
            <ArrowRight className="ml-3 w-6 h-6" data-id="element-468" />
          </a>
          <p className="mt-4 text-gray-500 text-sm" data-id="element-469">
            Secure checkout. Instant access.
          </p>
        </div>
      </div>
    </section>;
}